// src/components/features/campaigns/CampaignBudgetTab.jsx
import React, { useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Button,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  useTheme,
  alpha,
  ListItemIcon,
} from '@mui/material';
import {
  Add as AddIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  PieChart as PieChartIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CalendarToday as DateIcon,
} from '@mui/icons-material';

// Import services and hooks
import useCampaignStore from '../../stores/campaignStore';
import useNotificationStore from '../../stores/notificationStore';
import useAuthStore from '../../stores/authStore';

// Format currency helper
const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency 
  }).format(amount);
};

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

// Get platform name
const getPlatformName = (platform) => {
  const platformNames = {
    facebook: 'Facebook',
    instagram: 'Instagram',
    twitter: 'Twitter',
    linkedin: 'LinkedIn',
    youtube: 'YouTube',
    tiktok: 'TikTok',
    snapchat: 'Snapchat',
    googleAds: 'Google Ads',
    other: 'Other',
  };
  
  return platformNames[platform] || platform;
};

// Initial transaction form
const initialTransactionForm = {
  platform: '',
  amount: 0,
  description: '',
  date: new Date().toISOString().split('T')[0]
};

// CampaignBudgetTab component
const CampaignBudgetTab = ({ campaign }) => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [transactionForm, setTransactionForm] = useState({...initialTransactionForm});
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedTransactionIndex, setSelectedTransactionIndex] = useState(null);
  
  // Hooks
  const { addBudgetTransaction } = useCampaignStore();
  const { showSuccess, showError } = useNotificationStore();
  const { hasPermission } = useAuthStore();
  
  // Get campaign budget
  const budget = campaign?.budget || {
    total: 0,
    currency: 'USD',
    spent: 0,
    remaining: 0,
    allocation: {},
    transactions: []
  };
  
  // Check if user can manage budget
  const canManageBudget = hasPermission('campaigns', 'update');
  
  // Calculate allocation percentages
  const calculatePercentages = () => {
    const allocations = [];
    
    // Add platform allocations
    for (const platform in budget.allocation) {
      if (budget.allocation[platform]) {
        allocations.push({
          name: getPlatformName(platform),
          amount: budget.allocation[platform].amount || 0,
          percentage: budget.allocation[platform].percentage || 0,
          platform: platform
        });
      }
    }
    
    // Sort by percentage descending
    return allocations.sort((a, b) => b.percentage - a.percentage);
  };
  
  // Open transaction dialog
  const handleAddTransaction = () => {
    setTransactionForm({...initialTransactionForm});
    setOpenDialog(true);
  };
  
  // Close transaction dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setTransactionForm({...initialTransactionForm});
  };
  
  // Handle form field changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setTransactionForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Submit transaction
  const handleSubmitTransaction = async () => {
    // Validate form
    if (!transactionForm.platform || !transactionForm.amount || !transactionForm.description) {
      showError('Please fill in all required fields');
      return;
    }
    
    try {
      await addBudgetTransaction(campaign._id, transactionForm);
      showSuccess('Transaction added successfully');
      handleCloseDialog();
    } catch (error) {
      showError(error.message || 'Failed to add transaction');
    }
  };
  
  // Handle transaction menu
  const handleOpenMenu = (event, index) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedTransactionIndex(index);
  };
  
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setSelectedTransactionIndex(null);
  };
  
  // Calculate spent percentage
  const spentPercentage = budget.total > 0 ? (budget.spent / budget.total) * 100 : 0;
  
  // Calculate allocations
  const allocations = calculatePercentages();
  
  return (
    <Grid container spacing={3}>
      {/* Budget Summary Card */}
      <Grid item xs={12} md={6}>
        <Card elevation={0} variant="outlined">
          <CardHeader 
            title="Budget Summary" 
            avatar={<MoneyIcon  />}
          />
          <CardContent>
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Total Budget
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {formatCurrency(budget.total, budget.currency)}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Currency
                  </Typography>
                  <Typography variant="h6">
                    {budget.currency}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  Spent: {formatCurrency(budget.spent, budget.currency)}
                </Typography>
                <Typography variant="body2">
                  {spentPercentage.toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={spentPercentage} 
                sx={{ height: 8, borderRadius: 1 }}
                color={
                  spentPercentage > 100 ? 'error' :
                  spentPercentage > 90 ? 'warning' : 'primary'
                }
              />
            </Box>
            
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  Remaining: {formatCurrency(budget.remaining, budget.currency)}
                </Typography>
                <Typography variant="body2">
                  {(100 - spentPercentage).toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={100 - spentPercentage} 
                sx={{ height: 8, borderRadius: 1 }}
                color={
                  spentPercentage > 100 ? 'error' :
                  spentPercentage > 90 ? 'warning' : 'success'
                }
              />
            </Box>
            
            {canManageBudget && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  
                  startIcon={<AddIcon />}
                  onClick={handleAddTransaction}
                >
                  Add Transaction
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
      
      {/* Budget Allocation Card */}
      <Grid item xs={12} md={6}>
        <Card elevation={0} variant="outlined">
          <CardHeader 
            title="Budget Allocation" 
            avatar={<PieChartIcon  />}
          />
          <CardContent>
            {allocations.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Platform</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Percentage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allocations.map((allocation, index) => (
                      <TableRow key={index}>
                        <TableCell>{allocation.name}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(allocation.amount, budget.currency)}
                        </TableCell>
                        <TableCell align="right">
                          {allocation.percentage}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ 
                p: 3, 
                textAlign: 'center', 
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 1,
                border: `1px dashed ${theme.palette.divider}`
              }}>
                <Typography color="textSecondary">
                  No budget allocation defined
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
      
      {/* Transactions List */}
      <Grid item xs={12}>
        <Card elevation={0} variant="outlined">
          <CardHeader 
            title="Transaction History" 
            avatar={<ReceiptIcon  />}
          />
          <CardContent>
            {budget.transactions && budget.transactions.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Platform</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {budget.transactions.map((transaction, index) => (
                      <TableRow key={index}>
                        <TableCell>{formatDate(transaction.date)}</TableCell>
                        <TableCell>{getPlatformName(transaction.platform)}</TableCell>
                        <TableCell>{transaction.description}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(transaction.amount, budget.currency)}
                        </TableCell>
                        <TableCell align="right">
                          {canManageBudget && (
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenMenu(e, index)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ 
                p: 3, 
                textAlign: 'center', 
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 1,
                border: `1px dashed ${theme.palette.divider}`
              }}>
                <Typography color="textSecondary">
                  No transactions recorded
                </Typography>
                {canManageBudget && (
                  <Button
                    variant="contained"
                    
                    startIcon={<AddIcon />}
                    onClick={handleAddTransaction}
                    sx={{ mt: 2 }}
                  >
                    Add First Transaction
                  </Button>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
      
      {/* Transaction Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={handleCloseMenu}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit Transaction
        </MenuItem>
        <MenuItem onClick={handleCloseMenu}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <Typography color="error">Delete</Typography>
        </MenuItem>
      </Menu>
      
      {/* Transaction Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add Budget Transaction
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="platform-label">Platform</InputLabel>
                <Select
                  labelId="platform-label"
                  id="platform"
                  name="platform"
                  value={transactionForm.platform}
                  onChange={handleFormChange}
                  label="Platform"
                  required
                >
                  {Object.keys(campaign?.platforms || {})
                    .filter(platform => campaign?.platforms[platform] === true || platform === 'other')
                    .map(platform => (
                      <MenuItem key={platform} value={platform}>
                        {getPlatformName(platform)}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Amount"
                name="amount"
                type="number"
                value={transactionForm.amount}
                onChange={handleFormChange}
                fullWidth
                required
                margin="normal"
                InputProps={{
                  startAdornment: <MoneyIcon color="action" sx={{ mr: 1 }} />,
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                value={transactionForm.description}
                onChange={handleFormChange}
                fullWidth
                required
                margin="normal"
                placeholder="e.g., Facebook Ad Campaign, Video Production, etc."
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Date"
                name="date"
                type="date"
                value={transactionForm.date}
                onChange={handleFormChange}
                fullWidth
                margin="normal"
                InputProps={{
                  startAdornment: <DateIcon color="action" sx={{ mr: 1 }} />,
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitTransaction} 
            variant="contained" 
            
            disabled={!transactionForm.platform || !transactionForm.amount || !transactionForm.description}
          >
            Add Transaction
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default CampaignBudgetTab;